export const PYTHON_PROBLEMS = [
  
    {
      id: 1,
      title: "Hello World",
      slug: "python-hello-world-print-function",
      seoTitle: "Python Hello World | Print Function Basics | Repractiq",
      metaDescription: "Learn to use Python's print() function by writing your first Hello World program. A foundational Python exercise for beginners.",
      tags: ["print", "output", "beginner"],
      difficulty: "Easy",
      type: "output",
      description: "Write a Python program that prints 'Hello, World!' to the screen.",
      explanation: "The print() function outputs text to the console. It is the most basic Python function and the starting point for every Python developer.",
      hint: "Use the print() function.",
      solutionCode: `print("Hello, World!")`,
      expectedOutput: "Hello, World!",
      testCases: [],
    },
    {
      id: 2,
      title: "Sum of Two Numbers",
      slug: "python-sum-two-numbers-function",
      seoTitle: "Python Sum Function | Add Two Numbers | Repractiq",
      metaDescription: "Practice writing a Python function that takes two numbers and returns their sum. Covers function definition and the + operator.",
      tags: ["functions", "arithmetic", "return"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `add` that takes two numbers `a` and `b` and returns their sum.",
      explanation: "Functions in Python are defined using `def`. The `return` statement sends the calculated value back. You can call `print(add(2, 3))` to test your function locally in the editor.",
      hint: "Use the `+` operator inside your function: `return a + b`",
      solutionCode: `def add(a, b):
    return a + b

# Example call:
print(add(2, 3))  # Expected output: 5`,
      expectedOutput: "",
      testCases: [
        { input: "a = 2, b = 3", call: "add(2, 3)", expected: "5" },
        { input: "a = 10, b = 20", call: "add(10, 20)", expected: "30" },
        { input: "a = -1, b = 1", call: "add(-1, 1)", expected: "0" },
      ],
    },
    {
      id: 3,
      title: "Even or Odd",
      slug: "python-even-odd-modulo-operator",
      seoTitle: "Python Even or Odd | Modulo Operator | Repractiq",
      metaDescription: "Write a Python function to check if a number is even or odd using the modulo operator. A classic beginner Python exercise.",
      tags: ["conditionals", "modulo", "functions"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `even_or_odd` that takes a number and returns 'Even' if it's even, 'Odd' if it's odd.",
      explanation: "The modulo operator % returns the remainder after division. If n % 2 equals 0 the number is even, otherwise it is odd.",
      hint: "Use the modulo operator % to check divisibility by 2.",
      solutionCode: `def even_or_odd(n):\n    return "Even" if n % 2 == 0 else "Odd"`,
      expectedOutput: "",
      testCases: [
        { input: "4", call: "even_or_odd(4)", expected: "Even" },
        { input: "7", call: "even_or_odd(7)", expected: "Odd" },
        { input: "0", call: "even_or_odd(0)", expected: "Even" },
      ],
    },
    {
      id: 4,
      title: "Reverse a String",
      slug: "python-reverse-string-slicing",
      seoTitle: "Python Reverse String | String Slicing | Repractiq",
      metaDescription: "Learn to reverse a string in Python using slicing. A common interview question and essential string manipulation technique.",
      tags: ["strings", "slicing", "functions"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `reverse_string` that takes a string and returns it reversed.",
      explanation: "Python string slicing with s[::-1] creates a reversed copy. The -1 step means iterate backwards through the string.",
      hint: "Use Python string slicing — s[::-1]",
      solutionCode: `def reverse_string(s):\n    return s[::-1]`,
      expectedOutput: "",
      testCases: [
        { input: "'hello'",  call: "reverse_string('hello')",  expected: "olleh" },
        { input: "'Python'", call: "reverse_string('Python')", expected: "nohtyP" },
        { input: "'12345'",  call: "reverse_string('12345')",  expected: "54321" },
      ],
    },
    {
      id: 5,
      title: "List Maximum",
      slug: "python-find-max-in-list",
      seoTitle: "Python Find Maximum Value in List | Repractiq",
      metaDescription: "Write a Python function to find the largest number in a list. Covers Python's built-in max() function and list iteration.",
      tags: ["lists", "max", "built-ins"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `find_max` that takes a list of numbers and returns the largest number.",
      explanation: "Python's built-in max() function returns the largest item in an iterable. You can also loop through the list manually to find the maximum.",
      hint: "Python has a built-in max() function.",
      solutionCode: `def find_max(numbers):\n    return max(numbers)`,
      expectedOutput: "",
      testCases: [
        { input: "[3,1,4,1,5]", call: "find_max([3,1,4,1,5])", expected: "5" },
        { input: "[10,20,15]",  call: "find_max([10,20,15])",  expected: "20" },
        { input: "[-5,-1,-3]",  call: "find_max([-5,-1,-3])",  expected: "-1" },
      ],
    },
    {
      id: 6,
      title: "Count Vowels",
      slug: "python-count-vowels-in-string",
      seoTitle: "Python Count Vowels in String | Repractiq",
      metaDescription: "Write a Python function that counts the number of vowels in a string. Covers string iteration and conditional logic.",
      tags: ["strings", "loops", "conditionals"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `count_vowels` that takes a string and returns the number of vowels (a, e, i, o, u) in it.",
      explanation: "Loop through each character and check if it is in the string 'aeiou'. Use lower() to handle uppercase letters.",
      hint: "Loop through the string and check if each character is in 'aeiou'.",
      solutionCode: `def count_vowels(s):\n    return sum(1 for char in s.lower() if char in 'aeiou')`,
      expectedOutput: "",
      testCases: [
        { input: "'hello'",  call: "count_vowels('hello')",  expected: "2" },
        { input: "'Python'", call: "count_vowels('Python')", expected: "1" },
        { input: "'aeiou'",  call: "count_vowels('aeiou')",  expected: "5" },
      ],
    },
    {
      id: 7,
      title: "FizzBuzz",
      slug: "python-fizzbuzz-classic-interview-problem",
      seoTitle: "Python FizzBuzz | Classic Interview Problem | Repractiq",
      metaDescription: "Solve the classic FizzBuzz problem in Python. A must-know interview question testing conditionals and modulo operator.",
      tags: ["conditionals", "modulo", "interview"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `fizzbuzz` that takes a number n and returns 'Fizz' if divisible by 3, 'Buzz' if divisible by 5, 'FizzBuzz' if both, otherwise the number as a string.",
      explanation: "Check FizzBuzz before Fizz and Buzz to avoid missing the combined case. Use str(n) to convert the number to a string for the default case.",
      hint: "Check FizzBuzz before Fizz and Buzz separately.",
      solutionCode: `def fizzbuzz(n):\n    if n % 3 == 0 and n % 5 == 0:\n        return "FizzBuzz"\n    elif n % 3 == 0:\n        return "Fizz"\n    elif n % 5 == 0:\n        return "Buzz"\n    return str(n)`,
      expectedOutput: "",
      testCases: [
        { input: "3",  call: "fizzbuzz(3)",  expected: "Fizz" },
        { input: "5",  call: "fizzbuzz(5)",  expected: "Buzz" },
        { input: "15", call: "fizzbuzz(15)", expected: "FizzBuzz" },
        { input: "7",  call: "fizzbuzz(7)",  expected: "7" },
      ],
    },
    {
      id: 8,
      title: "Filter Even Numbers",
      slug: "python-filter-even-numbers-list-comprehension",
      seoTitle: "Python Filter Even Numbers | List Comprehension | Repractiq",
      metaDescription: "Write a Python function that filters even numbers from a list using list comprehension. A core Python technique for data filtering.",
      tags: ["lists", "list comprehension", "filter"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `filter_evens` that takes a list of numbers and returns only the even numbers using list comprehension.",
      explanation: "List comprehension with a condition [x for x in numbers if x % 2 == 0] creates a new list with only the elements that satisfy the condition.",
      hint: "Use [x for x in numbers if x % 2 == 0]",
      solutionCode: `def filter_evens(numbers):\n    return [x for x in numbers if x % 2 == 0]`,
      expectedOutput: "",
      testCases: [
        { input: "[1,2,3,4,5,6]", call: "str(filter_evens([1,2,3,4,5,6]))", expected: "[2, 4, 6]" },
        { input: "[1,3,5]",       call: "str(filter_evens([1,3,5]))",       expected: "[]" },
        { input: "[2,4,6]",       call: "str(filter_evens([2,4,6]))",       expected: "[2, 4, 6]" },
      ],
    },
    {
      id: 9,
      title: "Word Count",
      slug: "python-word-count-dictionary",
      seoTitle: "Python Word Count | Dictionary Counting | Repractiq",
      metaDescription: "Write a Python function that counts word occurrences in a string using a dictionary. Covers string splitting and dictionary operations.",
      tags: ["dictionaries", "strings", "counting"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `word_count` that takes a string and returns a dictionary with each word as a key and its count as the value.",
      explanation: "Split the string into words with .split(), then use a dictionary to count occurrences. The get() method with a default of 0 makes counting clean.",
      hint: "Split the string into words and use a dictionary to count.",
      solutionCode: `def word_count(text):\n    counts = {}\n    for word in text.split():\n        counts[word] = counts.get(word, 0) + 1\n    return counts`,
      expectedOutput: "",
      testCases: [
        { input: "'hello world hello'", call: "word_count('hello world hello')", expected: "{'hello': 2, 'world': 1}" },
        { input: "'a a a'",             call: "word_count('a a a')",             expected: "{'a': 3}" },
      ],
    },
    {
      id: 10,
      title: "Factorial",
      slug: "python-factorial-recursion",
      seoTitle: "Python Factorial | Recursion and Loops | Repractiq",
      metaDescription: "Write a Python function to calculate the factorial of a number using recursion or loops. A classic problem testing Python fundamentals.",
      tags: ["recursion", "math", "functions"],
      difficulty: "Easy",
      type: "function",
      description: "Write a function called `factorial` that takes a non-negative integer n and returns n! (n factorial).",
      explanation: "Factorial of 0 is 1 by definition. For n > 0, multiply n by factorial(n-1). You can also use a loop or math.factorial().",
      hint: "Factorial of 0 is 1. For n > 0, multiply n by factorial(n-1).",
      solutionCode: `def factorial(n):\n    if n == 0:\n        return 1\n    return n * factorial(n - 1)`,
      expectedOutput: "",
      testCases: [
        { input: "0", call: "factorial(0)", expected: "1" },
        { input: "5", call: "factorial(5)", expected: "120" },
        { input: "3", call: "factorial(3)", expected: "6" },
      ],
    },
      {
        id: 11,
        title: "Sum of List Elements",
        slug: "python-sum-list-elements",
        seoTitle: "Python Sum of List Elements | Basic Iteration | Repractiq",
        metaDescription: "Learn to sum elements of a list in Python using iteration and the built-in sum() function. Perfect practice for beginner programmers.",
        tags: ["lists", "loops", "arithmetic"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `sum_list` that takes a list of numbers and returns their total sum.",
        explanation: "Python's built-in sum() function returns the sum of all elements in an iterable. Alternatively, you can iterate through the list using a loop and accumulate the sum.",
        hint: "You can use Python's built-in sum() function or loop through the list manually.",
        solutionCode: `def sum_list(numbers):\n    return sum(numbers)`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3, 4]", call: "sum_list([1, 2, 3, 4])", expected: "10" },
          { input: "[10, -5, 5]",  call: "sum_list([10, -5, 5])",  expected: "10" },
          { input: "[]",           call: "sum_list([])",           expected: "0" },
        ],
      },
      {
        id: 12,
        title: "Celsius to Fahrenheit",
        slug: "python-celsius-to-fahrenheit-converter",
        seoTitle: "Python Celsius to Fahrenheit Converter | Repractiq",
        metaDescription: "Convert temperature from Celsius to Fahrenheit in Python. Understand basic mathematical operations and formula application.",
        tags: ["math", "variables", "functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `celsius_to_fahrenheit` that takes a temperature in Celsius and returns it in Fahrenheit.",
        explanation: "The formula to convert Celsius to Fahrenheit is: F = (C * 9/5) + 32. Multiply the input by 1.8 and add 32.",
        hint: "Use the formula: (celsius * 9/5) + 32.",
        solutionCode: `def celsius_to_fahrenheit(c):\n    return (c * 9/5) + 32`,
        expectedOutput: "",
        testCases: [
          { input: "0",   call: "celsius_to_fahrenheit(0)",   expected: "32.0" },
          { input: "100", call: "celsius_to_fahrenheit(100)", expected: "212.0" },
          { input: "-40", call: "celsius_to_fahrenheit(-40)", expected: "-40.0" },
        ],
      },
      {
        id: 13,
        title: "Is Palindrome",
        slug: "python-is-palindrome-string",
        seoTitle: "Python Palindrome Checker | String Methods | Repractiq",
        metaDescription: "Write a Python function to check if a string is a palindrome. Learn basic string manipulation and slicing techniques.",
        tags: ["strings", "slicing", "conditionals"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `is_palindrome` that takes a string and returns True if it reads the same forward and backward, and False otherwise.",
        explanation: "A string is a palindrome if it is equal to its reversed self. We can reverse a string cleanly in Python using slicing with a step of -1: s[::-1].",
        hint: "Compare the original string with its reversed slice: s == s[::-1].",
        solutionCode: `def is_palindrome(s):\n    return s == s[::-1]`,
        expectedOutput: "",
        testCases: [
          { input: "'racecar'", call: "is_palindrome('racecar')", expected: "True" },
          { input: "'hello'",   call: "is_palindrome('hello')",   expected: "False" },
          { input: "'radar'",   call: "is_palindrome('radar')",   expected: "True" },
        ],
      },
      {
        id: 14,
        title: "Length of List (Manual)",
        slug: "python-length-of-list-without-len",
        seoTitle: "Python Length of List | Custom Loop Implementation | Repractiq",
        metaDescription: "Learn how to calculate the length of a list in Python without using the len() function by utilizing loop structures.",
        tags: ["lists", "loops", "iteration"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `get_length` that takes a list and returns the number of items in it. Do not use the built-in `len()` function.",
        explanation: "Initialize a counter variable to 0. Loop through each element in the list, incrementing the counter by 1 for each element, then return the counter.",
        hint: "Initialize a counter to 0, run a for-loop over the list, incrementing the counter each time.",
        solutionCode: `def get_length(lst):\n    count = 0\n    for _ in lst:\n        count += 1\n    return count`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 20, 30]", call: "get_length([10, 20, 30])", expected: "3" },
          { input: "[]",           call: "get_length([])",           expected: "0" },
          { input: "['a', 'b']",   call: "get_length(['a', 'b'])",   expected: "2" },
        ],
      },
      {
        id: 15,
        title: "Merge Two Dictionaries",
        slug: "python-merge-two-dictionaries",
        seoTitle: "Python Merge Dictionaries | Dictionary Operations | Repractiq",
        metaDescription: "Learn how to merge two dictionaries in Python. Master dictionary unpacking and update methods.",
        tags: ["dictionaries", "functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `merge_dicts` that takes two dictionaries and returns a single merged dictionary.",
        explanation: "You can copy the first dictionary and use the .update() method with the second, or use dictionary unpacking: {**dict1, **dict2}.",
        hint: "Use Python's double asterisk dictionary unpacking syntax: {**dict1, **dict2}.",
        solutionCode: `def merge_dicts(dict1, dict2):\n    return {**dict1, **dict2}`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': 1}, {'b': 2}", call: "merge_dicts({'a': 1}, {'b': 2})", expected: "{'a': 1, 'b': 2}" },
          { input: "{'x': 10}, {'x': 20}", call: "merge_dicts({'x': 10}, {'x': 20})", expected: "{'x': 20}" },
        ],
      },
      {
        id: 16,
        title: "Minutes to Seconds",
        slug: "python-convert-minutes-to-seconds",
        seoTitle: "Python Minutes to Seconds | Basic Math | Repractiq",
        metaDescription: "Learn basic mathematical conversions by creating a Python function to convert minutes to seconds.",
        tags: ["math", "functions"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `minutes_to_seconds` that takes an integer representing minutes and returns it converted to seconds.",
        explanation: "Multiply the input minutes by 60 to calculate the corresponding value in seconds.",
        hint: "Multiply the parameter by 60.",
        solutionCode: `def minutes_to_seconds(minutes):\n    return minutes * 60`,
        expectedOutput: "",
        testCases: [
          { input: "5", call: "minutes_to_seconds(5)", expected: "300" },
          { input: "0", call: "minutes_to_seconds(0)", expected: "0" },
          { input: "10", call: "minutes_to_seconds(10)", expected: "600" },
        ],
      },
      {
        id: 17,
        title: "Find Common Elements",
        slug: "python-find-common-elements-sets",
        seoTitle: "Python Common Elements | Sets and Intersections | Repractiq",
        metaDescription: "Identify common elements between two Python lists using set intersections or standard list iteration.",
        tags: ["lists", "sets", "intersection"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `common_elements` that takes two lists and returns a list containing only the elements common to both.",
        explanation: "By converting both lists to sets, you can find their intersection using set1.intersection(set2) or the & operator, then convert it back to a list.",
        hint: "Convert the lists into sets and use the intersection operator &.",
        solutionCode: `def common_elements(list1, list2):\n    return list(set(list1) & set(list2))`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3], [2, 3, 4]", call: "sorted(common_elements([1, 2, 3], [2, 3, 4]))", expected: "[2, 3]" },
          { input: "[1, 5], [2, 4]",       call: "common_elements([1, 5], [2, 4])",             expected: "[]" },
        ],
      },
      {
        id: 18,
        title: "Remove Duplicates",
        slug: "python-remove-duplicates-list",
        seoTitle: "Python Remove Duplicates | Set Type Casting | Repractiq",
        metaDescription: "Write a Python function to remove duplicate elements from a list using sets. Essential data structure practice.",
        tags: ["lists", "sets", "uniqueness"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `remove_duplicates` that takes a list and returns a new list containing only its unique elements.",
        explanation: "Converting a list to a set automatically filters out duplicate elements. Convert the set back to a list to yield the final clean output.",
        hint: "Convert the input list to a set, then cast it back to a list using list().",
        solutionCode: `def remove_duplicates(lst):\n    return list(set(lst))`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 2, 3]", call: "sorted(remove_duplicates([1, 2, 2, 3]))", expected: "[1, 2, 3]" },
          { input: "['a', 'a']",    call: "remove_duplicates(['a', 'a'])",          expected: "['a']" },
        ],
      },
      {
        id: 19,
        title: "Power of a Number",
        slug: "python-calculate-power-exponentiation",
        seoTitle: "Python Power Calculation | Exponentiation Operator | Repractiq",
        metaDescription: "Calculate mathematical power in Python using exponentiation operator **. Clean, efficient coding practices.",
        tags: ["math", "operators"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `power` that takes a base and an exponent and returns the base raised to the power of exponent.",
        explanation: "Python utilizes the double asterisk (**) operator for exponentiation. For example, a ** b calculates a to the power of b.",
        hint: "Use the exponentiation operator **.",
        solutionCode: `def power(base, exponent):\n    return base ** exponent`,
        expectedOutput: "",
        testCases: [
          { input: "2, 3", call: "power(2, 3)", expected: "8" },
          { input: "5, 0", call: "power(5, 0)", expected: "1" },
          { input: "10, 2", call: "power(10, 2)", expected: "100" },
        ],
      },
      {
        id: 20,
        title: "Sum of Digits",
        slug: "python-sum-of-digits-loop",
        seoTitle: "Python Sum of Digits | Type Casting and Loops | Repractiq",
        metaDescription: "Write a function to sum the digits of an integer in Python using string casting or numeric loops.",
        tags: ["math", "type conversion", "loops"],
        difficulty: "Easy",
        type: "function",
        description: "Write a function called `sum_digits` that takes a positive integer n and returns the sum of its individual digits.",
        explanation: "Convert the number to a string to iterate through each character. Cast each character back to an integer and sum them up.",
        hint: "Convert the integer to a string, loop through each digit, convert it back to int, and add it to your sum.",
        solutionCode: `def sum_digits(n):\n    return sum(int(digit) for digit in str(n))`,
        expectedOutput: "",
        testCases: [
          { input: "123",  call: "sum_digits(123)",  expected: "6" },
          { input: "405",  call: "sum_digits(405)",  expected: "9" },
          { input: "9999", call: "sum_digits(9999)", expected: "36" },
        ],
      },
        {
          id: 21,
          title: "Leap Year Checker",
          slug: "python-leap-year-checker-conditionals",
          seoTitle: "Python Leap Year Checker | Conditional Logic | Repractiq",
          metaDescription: "Determine if a given year is a leap year in Python. Practice complex conditional operators and logical checks.",
          tags: ["conditionals", "modulo", "logic"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `is_leap_year` that takes a year and returns True if it is a leap year, and False otherwise.",
          explanation: "A year is a leap year if it is divisible by 4, except for century years (ending in 00), which must also be divisible by 400.",
          hint: "A year is leap if (year % 4 == 0 and year % 100 != 0) or (year % 400 == 0).",
          solutionCode: `def is_leap_year(year):\n    return (year % 4 == 0 and year % 100 != 0) or (year % 400 == 0)`,
          expectedOutput: "",
          testCases: [
            { input: "2020", call: "is_leap_year(2020)", expected: "True" },
            { input: "1900", call: "is_leap_year(1900)", expected: "False" },
            { input: "2000", call: "is_leap_year(2000)", expected: "True" },
          ],
        },
        {
          id: 22,
          title: "Capitalize Words",
          slug: "python-capitalize-words-title-method",
          seoTitle: "Python Capitalize Words | String Formatting Methods | Repractiq",
          metaDescription: "Learn to capitalize the first letter of every word in a string using Python's built-in title method or custom iteration.",
          tags: ["strings", "formatting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `capitalize_words` that takes a string and returns it with the first letter of each word capitalized.",
          explanation: "Python offers the string method title() which converts the first character of each word to uppercase.",
          hint: "Look into the built-in string title() method.",
          solutionCode: `def capitalize_words(s):\n    return s.title()`,
          expectedOutput: "",
          testCases: [
            { input: "'hello world'",     call: "capitalize_words('hello world')",     expected: "Hello World" },
            { input: "'python is cool'",  call: "capitalize_words('python is cool')",  expected: "Python Is Cool" },
          ],
        },
        {
          id: 23,
          title: "Find Minimum in List",
          slug: "python-find-minimum-list",
          seoTitle: "Python Find Minimum Value in List | Built-in min() | Repractiq",
          metaDescription: "Find the smallest value in a Python list using the min() function. Simple, efficient, and essential.",
          tags: ["lists", "min", "built-ins"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `find_min` that takes a list of numbers and returns the smallest number.",
          explanation: "Use Python's native min() function, which behaves identically to max() but retrieves the smallest value from an iterable.",
          hint: "Use Python's built-in min() function.",
          solutionCode: `def find_min(numbers):\n    return min(numbers)`,
          expectedOutput: "",
          testCases: [
            { input: "[3, 1, 4, 1, 5]", call: "find_min([3, 1, 4, 1, 5])", expected: "1" },
            { input: "[10, 20, -5]",    call: "find_min([10, 20, -5])",    expected: "-5" },
          ],
        },
        {
          id: 24,
          title: "Check Key in Dictionary",
          slug: "python-check-key-exists-dictionary",
          seoTitle: "Python Check Key Exists | Membership Operator | Repractiq",
          metaDescription: "Check if a key exists in a Python dictionary using the efficient 'in' membership operator.",
          tags: ["dictionaries", "membership", "operators"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `key_exists` that takes a dictionary and a key, and returns True if the key is present in the dictionary, and False otherwise.",
          explanation: "The 'in' operator performs a constant time O(1) membership check when looking up keys directly in a dictionary.",
          hint: "Use the 'in' operator (e.g., key in my_dict).",
          solutionCode: `def key_exists(d, key):\n    return key in d`,
          expectedOutput: "",
          testCases: [
            { input: "{'a': 1}, 'a'", call: "key_exists({'a': 1}, 'a')", expected: "True" },
            { input: "{'a': 1}, 'b'", call: "key_exists({'a': 1}, 'b')", expected: "False" },
          ],
        },
        {
          id: 25,
          title: "Calculate Average",
          slug: "python-calculate-average-list",
          seoTitle: "Python Calculate Average of List | sum() and len() | Repractiq",
          metaDescription: "Find the average value of elements in a Python list using basic arithmetic operators, sum, and len functions.",
          tags: ["lists", "arithmetic", "loops"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `calculate_average` that takes a list of numbers and returns their mathematical average. Return 0 if the list is empty.",
          explanation: "To find the average, sum the list and divide by its length. Include a safety check to return 0 if the list is empty to avoid ZeroDivisionError.",
          hint: "Sum the list elements and divide by the length of the list.",
          solutionCode: `def calculate_average(numbers):\n    return sum(numbers) / len(numbers) if numbers else 0`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 2, 3, 4]", call: "calculate_average([1, 2, 3, 4])", expected: "2.5" },
            { input: "[]",           call: "calculate_average([])",           expected: "0" },
            { input: "[10, 20, 30]", call: "calculate_average([10, 20, 30])", expected: "20.0" },
          ],
        },
        {
          id: 26,
          title: "Fahrenheit to Celsius",
          slug: "python-fahrenheit-to-celsius-converter",
          seoTitle: "Python Fahrenheit to Celsius | Formula Application | Repractiq",
          metaDescription: "Write a Python function to convert temperature from Fahrenheit to Celsius using basic math division and subtraction.",
          tags: ["math", "variables"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `fahrenheit_to_celsius` that takes a temperature in Fahrenheit and returns it in Celsius.",
          explanation: "Apply the standard formula: C = (F - 32) * 5/9. Pay attention to order of operations by using parentheses.",
          hint: "Use the formula: (fahrenheit - 32) * 5/9.",
          solutionCode: `def fahrenheit_to_celsius(f):\n    return (f - 32) * 5/9`,
          expectedOutput: "",
          testCases: [
            { input: "32",  call: "fahrenheit_to_celsius(32)",  expected: "0.0" },
            { input: "212", call: "fahrenheit_to_celsius(212)", expected: "100.0" },
          ],
        },
        {
          id: 27,
          title: "Count Substring Occurrences",
          slug: "python-count-substring-occurrences-string",
          seoTitle: "Python Count Substring | String count() Method | Repractiq",
          metaDescription: "Learn to find how many times a substring appears in a given parent string using Python's count() method.",
          tags: ["strings", "counting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `count_substring` that takes a parent string and a substring, and returns the number of times the substring appears.",
          explanation: "Use Python's built-in count() method available on string objects to match standard sub-patterns.",
          hint: "Use the built-in string method count() as: string.count(substring).",
          solutionCode: `def count_substring(s, sub):\n    return s.count(sub)`,
          expectedOutput: "",
          testCases: [
            { input: "'banana', 'an'", call: "count_substring('banana', 'an')", expected: "2" },
            { input: "'hello', 'world'", call: "count_substring('hello', 'world')", expected: "0" },
          ],
        },
        {
          id: 28,
          title: "List of Strings to String",
          slug: "python-convert-list-of-strings-to-string-join",
          seoTitle: "Python List to String Conversion | join() Method | Repractiq",
          metaDescription: "Write a Python function to merge a list of strings into a single unified string separated by spaces or characters.",
          tags: ["strings", "lists", "formatting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `join_strings` that takes a list of strings and a delimiter, and returns a single string with elements joined by that delimiter.",
          explanation: "Use the delimiter's .join() method passing the list. It efficiently concatenates iterable string data.",
          hint: "Call delimiter.join(list_of_strings).",
          solutionCode: `def join_strings(lst, delimiter):\n    return delimiter.join(lst)`,
          expectedOutput: "",
          testCases: [
            { input: "['a', 'b', 'c'], '-'", call: "join_strings(['a', 'b', 'c'], '-')", expected: "a-b-c" },
            { input: "['hello', 'world'], ' '", call: "join_strings(['hello', 'world'], ' ')", expected: "hello world" },
          ],
        },
        {
          id: 29,
          title: "Positive, Negative, or Zero",
          slug: "python-check-sign-of-number-conditionals",
          seoTitle: "Python Sign Checker | Multi-Way Conditionals | Repractiq",
          metaDescription: "Evaluate whether a number is positive, negative, or zero in Python using if, elif, and else statements.",
          tags: ["conditionals", "logic"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `check_sign` that takes a number and returns 'Positive' if the number is greater than 0, 'Negative' if it is less than 0, and 'Zero' if it is equal to 0.",
          explanation: "Implement an if-elif-else logic branch structure to evaluate mathematical comparison constraints.",
          hint: "Use an if/elif/else block checking > 0, < 0, and == 0.",
          solutionCode: `def check_sign(num):\n    if num > 0:\n        return "Positive"\n    elif num < 0:\n        return "Negative"\n    else:\n        return "Zero"`,
          expectedOutput: "",
          testCases: [
            { input: "5",  call: "check_sign(5)",  expected: "Positive" },
            { input: "-3", call: "check_sign(-3)", expected: "Negative" },
            { input: "0",  call: "check_sign(0)",  expected: "Zero" },
          ],
        },
        {
          id: 30,
          title: "Multiply List Elements",
          slug: "python-multiply-all-list-elements",
          seoTitle: "Python Product of List | Loops and Multiplications | Repractiq",
          metaDescription: "Calculate the product of all elements in a Python list. Accumulate mathematical products inside basic loops.",
          tags: ["lists", "loops", "arithmetic"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `multiply_list` that takes a list of numbers and returns their product. If the list is empty, return 1.",
          explanation: "Initialize an accumulator variable to 1. Traverse through the elements multiplying each against the running accumulator.",
          hint: "Start with a variable equal to 1, loop through each element, and multiply the accumulator.",
          solutionCode: `def multiply_list(numbers):\n    result = 1\n    for num in numbers:\n        result *= num\n    return result`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 2, 3, 4]", call: "multiply_list([1, 2, 3, 4])", expected: "24" },
            { input: "[5, 0, 10]",   call: "multiply_list([5, 0, 10])",   expected: "0" },
            { input: "[]",           call: "multiply_list([])",           expected: "1" },
          ],
        },
        {
          id: 31,
          title: "Generate Range List",
          slug: "python-generate-range-list-function",
          seoTitle: "Python Generate Range List | Built-in range() | Repractiq",
          metaDescription: "Create custom Python list sequences easily using the flexible range() function and type casting.",
          tags: ["lists", "built-ins"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `generate_range` that takes a start and a stop value, and returns a list containing numbers from start up to (but excluding) stop.",
          explanation: "Pass parameters directly to the range(start, stop) constructor and wrap the result in list() to cast the generator output.",
          hint: "Use list(range(start, stop)).",
          solutionCode: `def generate_range(start, stop):\n    return list(range(start, stop))`,
          expectedOutput: "",
          testCases: [
            { input: "1, 5",  call: "generate_range(1, 5)",  expected: "[1, 2, 3, 4]" },
            { input: "10, 12", call: "generate_range(10, 12)", expected: "[10, 11]" },
          ],
        },
        {
          id: 32,
          title: "Anagram Checker",
          slug: "python-anagram-checker-sorted-strings",
          seoTitle: "Python Anagram Checker | Sorting Strings | Repractiq",
          metaDescription: "Compare character composition of two strings in Python to check if they are anagrams by comparing their sorted values.",
          tags: ["strings", "sorting", "functions"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `is_anagram` that takes two strings and returns True if they are anagrams (contain identical letters in different orders), and False otherwise.",
          explanation: "Sort each string using the built-in sorted() function. If both strings yield identical sorted character arrays, they are anagrams.",
          hint: "Compare the sorted characters of both strings: sorted(str1) == sorted(str2).",
          solutionCode: `def is_anagram(str1, str2):\n    return sorted(str1) == sorted(str2)`,
          expectedOutput: "",
          testCases: [
            { input: "'listen', 'silent'", call: "is_anagram('listen', 'silent')", expected: "True" },
            { input: "'hello', 'world'",   call: "is_anagram('hello', 'world')",   expected: "False" },
          ],
        },
        {
          id: 33,
          title: "Swap Two Variables",
          slug: "python-swap-two-variables-tuple-packing",
          seoTitle: "Python Swap Variables | Tuple Assignment Tricks | Repractiq",
          metaDescription: "Swap two variable values in Python. Learn safe temporary variable swapping and native tuple-unpacking expressions.",
          tags: ["variables", "tuples"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `swap_values` that takes a and b, and returns them swapped as a tuple (b, a).",
          explanation: "Python allows clean assignment swapping using structural tuple assignments: a, b = b, a.",
          hint: "Return a tuple containing b and a in swapped order.",
          solutionCode: `def swap_values(a, b):\n    return b, a`,
          expectedOutput: "",
          testCases: [
            { input: "5, 10",   call: "swap_values(5, 10)",   expected: "(10, 5)" },
            { input: "'x', 'y'", call: "swap_values('x', 'y')", expected: "('y', 'x')" },
          ],
        },
        {
          id: 34,
          title: "Count Characters",
          slug: "python-count-characters-dictionary",
          seoTitle: "Python Count Character Occurrences | Dictionary Maps | Repractiq",
          metaDescription: "Count letter counts inside Python strings. Master key-value dictionary assignments and tracking algorithms.",
          tags: ["dictionaries", "strings", "counting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `count_chars` that takes a string and returns a dictionary of character counts.",
          explanation: "Loop through each character. Check if it's in the dict: if it is, increment by 1, otherwise initialize to 1.",
          hint: "Iterate through the string, writing character occurrences to an empty tracking dictionary.",
          solutionCode: `def count_chars(s):\n    counts = {}\n    for char in s:\n        counts[char] = counts.get(char, 0) + 1\n    return counts`,
          expectedOutput: "",
          testCases: [
            { input: "'aba'", call: "count_chars('aba')", expected: "{'a': 2, 'b': 1}" },
            { input: "'c'",   call: "count_chars('c')",   expected: "{'c': 1}" },
          ],
        },
        {
          id: 35,
          title: "Check Substring",
          slug: "python-check-substring-membership",
          seoTitle: "Python Is Substring Present | Membership Operators | Repractiq",
          metaDescription: "Detect if a small substring resides within a parent string sequence using Python 'in' operator rules.",
          tags: ["strings", "membership", "operators"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `has_substring` that takes a parent string and a sub-string, and returns True if the substring is present, and False otherwise.",
          explanation: "Use Python's native 'in' operator for strings. It is clear, concise, and optimized for performance.",
          hint: "Use the syntax: sub_string in parent_string.",
          solutionCode: `def has_substring(parent, sub):\n    return sub in parent`,
          expectedOutput: "",
          testCases: [
            { input: "'apple', 'app'", call: "has_substring('apple', 'app')", expected: "True" },
            { input: "'apple', 'or'",  call: "has_substring('apple', 'or')",  expected: "False" },
          ],
        },
        {
          id: 36,
          title: "Element Search in List",
          slug: "python-element-search-list-membership",
          seoTitle: "Python List Search | Element Membership Checks | Repractiq",
          metaDescription: "Look for targets inside sequence blocks using high performance 'in' logic operators in Python.",
          tags: ["lists", "membership"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `element_in_list` that checks if a specified target element exists within a list.",
          explanation: "Using the 'in' operator performs a search loop internally, returning True if found, and False otherwise.",
          hint: "Return target in list_variable.",
          solutionCode: `def element_in_list(lst, target):\n    return target in lst`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 2, 3], 2", call: "element_in_list([1, 2, 3], 2)", expected: "True" },
            { input: "[1, 2, 3], 5", call: "element_in_list([1, 2, 3], 5)", expected: "False" },
          ],
        },
        {
          id: 37,
          title: "Dictionary Keys as List",
          slug: "python-get-dictionary-keys-list",
          seoTitle: "Python Get Dictionary Keys as List | Repractiq",
          metaDescription: "Write a Python function to extract all keys from a dictionary and return them as a standard list structure.",
          tags: ["dictionaries", "lists"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `get_keys` that takes a dictionary and returns a list of all its keys.",
          explanation: "Access the dictionary's .keys() view object and convert it to a standard Python list using the list() function.",
          hint: "Convert dictionary.keys() to a list using the list() constructor.",
          solutionCode: `def get_keys(d):\n    return list(d.keys())`,
          expectedOutput: "",
          testCases: [
            { input: "{'a': 1, 'b': 2}", call: "sorted(get_keys({'a': 1, 'b': 2}))", expected: "['a', 'b']" },
            { input: "{}",               call: "get_keys({})",                      expected: "[]" },
          ],
        },
        {
          id: 38,
          title: "Sort a List of Numbers",
          slug: "python-sort-list-numbers",
          seoTitle: "Python Sort List | Built-in sorted() and .sort() | Repractiq",
          metaDescription: "Sort numbers in ascending order with Python using the built-in sorted() function or sort() list methods.",
          tags: ["lists", "sorting", "built-ins"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `sort_numbers` that takes a list of numbers and returns a new sorted list in ascending order.",
          explanation: "Use the built-in sorted() function, which takes an iterable and returns a new sorted list without modifying the original.",
          hint: "Use sorted(list_of_numbers) to return a new sorted list.",
          solutionCode: `def sort_numbers(numbers):\n    return sorted(numbers)`,
          expectedOutput: "",
          testCases: [
            { input: "[3, 1, 2]", call: "sort_numbers([3, 1, 2])", expected: "[1, 2, 3]" },
            { input: "[10, -1, 5]", call: "sort_numbers([10, -1, 5])", expected: "[-1, 5, 10]" },
          ],
        },
        {
          id: 39,
          title: "Absolute Value Checker",
          slug: "python-absolute-value-checker",
          seoTitle: "Python Absolute Value | Built-in abs() vs Loops | Repractiq",
          metaDescription: "Learn to compute absolute numerical values in Python with native built-ins and custom arithmetic check steps.",
          tags: ["math", "conditionals"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `get_absolute` that takes a number and returns its absolute value. Do not use the built-in `abs()` function.",
          explanation: "If a number is less than 0, negate it (return -num). Otherwise, return the number itself.",
          hint: "If the number is negative, return it multiplied by -1; else return the number.",
          solutionCode: `def get_absolute(num):\n    return -num if num < 0 else num`,
          expectedOutput: "",
          testCases: [
            { input: "-5", call: "get_absolute(-5)", expected: "5" },
            { input: "10", call: "get_absolute(10)", expected: "10" },
            { input: "0",  call: "get_absolute(0)",  expected: "0" },
          ],
        },
        {
          id: 40,
          title: "Repeat String N Times",
          slug: "python-repeat-string-n-times",
          seoTitle: "Python Repeat String | Multiplication Operators | Repractiq",
          metaDescription: "Multiply string sequences inside Python with native operators. An elegant string repetition approach.",
          tags: ["strings", "operators"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `repeat_string` that takes a string s and an integer n, and returns the string repeated n times.",
          explanation: "Python supports string repetition using the multiplication operator (*). For example, 'abc' * 3 evaluates to 'abcabcabc'.",
          hint: "Multiply the string by the number: s * n.",
          solutionCode: `def repeat_string(s, n):\n    return s * n`,
          expectedOutput: "",
          testCases: [
            { input: "'hi', 3",  call: "repeat_string('hi', 3)",  expected: "hihihi" },
            { input: "'abc', 1", call: "repeat_string('abc', 1)", expected: "abc" },
            { input: "'x', 0",   call: "repeat_string('x', 0)",   expected: "" },
          ],
        },
        {
          id: 41,
          title: "Count Occurrences in List",
          slug: "python-count-occurrences-in-list",
          seoTitle: "Python Count List Occurrences | Built-in count() | Repractiq",
          metaDescription: "Learn to count how many times an item appears in a Python list using the native count() method or a simple loop.",
          tags: ["lists", "counting", "built-ins"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `count_occurrences` that takes a list and a target item, and returns the number of times the target appears in the list.",
          explanation: "Python lists have a built-in .count() method that returns the total occurrences of a specific element inside the list structure.",
          hint: "Use the list.count(target) method.",
          solutionCode: `def count_occurrences(lst, target):\n    return lst.count(target)`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 2, 3, 2, 2], 2", call: "count_occurrences([1, 2, 3, 2, 2], 2)", expected: "3" },
            { input: "['a', 'b', 'c'], 'z'", call: "count_occurrences(['a', 'b', 'c'], 'z')", expected: "0" },
          ],
        },
        {
          id: 42,
          title: "Square List Elements",
          slug: "python-square-list-elements-comprehension",
          seoTitle: "Python Square List Elements | List Comprehension | Repractiq",
          metaDescription: "Write a Python function to square every number in a list using an optimized list comprehension expression.",
          tags: ["lists", "list comprehension", "math"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `square_numbers` that takes a list of numbers and returns a new list containing the square of each number using list comprehension.",
          explanation: "List comprehensions provide a concise syntax: [x**2 for x in numbers] creates a new list with each element raised to the power of 2.",
          hint: "Use a list comprehension expression like [x**2 for x in numbers].",
          solutionCode: `def square_numbers(numbers):\n    return [x**2 for x in numbers]`,
          expectedOutput: "",
          testCases: [
            { input: "[1, 2, 3]", call: "str(square_numbers([1, 2, 3]))", expected: "[1, 4, 9]" },
            { input: "[-2, 0]",   call: "str(square_numbers([-2, 0]))",   expected: "[4, 0]" },
          ],
        },
        {
          id: 43,
          title: "Find Index of Element",
          slug: "python-find-index-of-element-list",
          seoTitle: "Python Find Element Index | List index() Method | Repractiq",
          metaDescription: "Find the position index of an element in a Python list. Learn to use the index() method safely.",
          tags: ["lists", "searching"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `find_index` that takes a list and a target item, and returns the 0-based index of the target. If the item is not found, return -1.",
          explanation: "Use the list.index(target) method if the target exists in the list. Check membership first with 'in' to avoid raising a ValueError.",
          hint: "Check if the item is in the list before calling list.index(target).",
          solutionCode: `def find_index(lst, target):\n    if target in lst:\n        return lst.index(target)\n    return -1`,
          expectedOutput: "",
          testCases: [
            { input: "[10, 20, 30], 20", call: "find_index([10, 20, 30], 20)", expected: "1" },
            { input: "[10, 20, 30], 99", call: "find_index([10, 20, 30], 99)", expected: "-1" },
          ],
        },
        {
          id: 44,
          title: "String Length Checker",
          slug: "python-string-length-checker-len",
          seoTitle: "Python String Length | Built-in len() Function | Repractiq",
          metaDescription: "Determine the exact character count of a string using Python's native len() utility function.",
          tags: ["strings", "built-ins"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `string_length` that takes a string and returns its total number of characters.",
          explanation: "The built-in len() function accurately computes the length of strings, lists, dictionaries, and all other basic collections.",
          hint: "Pass the string directly into the len() function.",
          solutionCode: `def string_length(s):\n    return len(s)`,
          expectedOutput: "",
          testCases: [
            { input: "'hello'", call: "string_length('hello')", expected: "5" },
            { input: "''",      call: "string_length('')",      expected: "0" },
          ],
        },
        {
          id: 45,
          title: "Remove Whitespace",
          slug: "python-remove-whitespace-strip",
          seoTitle: "Python Remove Whitespace | String strip() Method | Repractiq",
          metaDescription: "Clean up user inputs by removing leading and trailing whitespaces using Python's string strip methods.",
          tags: ["strings", "formatting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `remove_whitespace` that takes a string and returns a copy with leading and trailing spaces removed.",
          explanation: "The string method .strip() returns a copy of the string with both leading and trailing whitespace characters completely stripped off.",
          hint: "Use the native string.strip() method.",
          solutionCode: `def remove_whitespace(s):\n    return s.strip()`,
          expectedOutput: "",
          testCases: [
            { input: "'  hello  '", call: "remove_whitespace('  hello  ')", expected: "hello" },
            { input: "'\\tworld\\n'", call: "remove_whitespace('\\tworld\\n')", expected: "world" },
          ],
        },
        {
          id: 46,
          title: "Check for Empty List",
          slug: "python-check-empty-list-conditionals",
          seoTitle: "Python Check Empty List | Implicit Boolean Trueness | Repractiq",
          metaDescription: "Learn Pythonic ways to verify if a list is empty using implicit boolean evaluation or length criteria.",
          tags: ["lists", "conditionals", "logic"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `is_list_empty` that takes a list and returns True if it contains no elements, and False otherwise.",
          explanation: "In Python, empty collections are implicitly False. You can check 'if not lst:' or verify if 'len(lst) == 0'.",
          hint: "Use 'not lst' or check if the length is equal to 0.",
          solutionCode: `def is_list_empty(lst):\n    return len(lst) == 0`,
          expectedOutput: "",
          testCases: [
            { input: "[]",        call: "is_list_empty([])",        expected: "True" },
            { input: "[1, 2, 3]", call: "is_list_empty([1, 2, 3])", expected: "False" },
          ],
        },
        {
          id: 47,
          title: "Convert String to Lowercase",
          slug: "python-convert-string-lowercase-lower",
          seoTitle: "Python String to Lowercase | lower() Method | Repractiq",
          metaDescription: "Convert strings entirely to lowercase using Python's native lower() method for uniform string matching.",
          tags: ["strings", "formatting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `to_lowercase` that takes a string and returns it converted completely to lowercase letters.",
          explanation: "The string method .lower() transforms all uppercase characters inside a string into their corresponding lowercase matches.",
          hint: "Call the built-in string.lower() method.",
          solutionCode: `def to_lowercase(s):\n    return s.lower()`,
          expectedOutput: "",
          testCases: [
            { input: "'PyThOn'", call: "to_lowercase('PyThOn')", expected: "python" },
            { input: "'HELLO'",  call: "to_lowercase('HELLO')",  expected: "hello" },
          ],
        },
        {
          id: 48,
          title: "Convert String to Uppercase",
          slug: "python-convert-string-uppercase-upper",
          seoTitle: "Python String to Uppercase | upper() Method | Repractiq",
          metaDescription: "Convert text characters completely to uppercase in Python using the native upper() method syntax.",
          tags: ["strings", "formatting"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `to_uppercase` that takes a string and returns it converted completely to uppercase letters.",
          explanation: "The string method .upper() converts every single lowercase letter inside a target string into an uppercase character.",
          hint: "Call the built-in string.upper() method.",
          solutionCode: `def to_uppercase(s):\n    return s.upper()`,
          expectedOutput: "",
          testCases: [
            { input: "'python'", call: "to_uppercase('python')", expected: "PYTHON" },
            { input: "'Hello'",  call: "to_uppercase('Hello')",  expected: "HELLO" },
          ],
        },
        {
          id: 49,
          title: "Dictionary Values as List",
          slug: "python-get-dictionary-values-list",
          seoTitle: "Python Get Dictionary Values | values() Method | Repractiq",
          metaDescription: "Extract all values from a Python dictionary map object and return them collected as a standard list sequence.",
          tags: ["dictionaries", "lists"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `get_values` that takes a dictionary and returns a list containing all its stored values.",
          explanation: "Access the collection via the dictionary's .values() method and pass it directly to list() to format the structural view output.",
          hint: "Convert dictionary.values() into a list using the list() constructor.",
          solutionCode: `def get_values(d):\n    return list(d.values())`,
          expectedOutput: "",
          testCases: [
            { input: "{'a': 1, 'b': 2}", call: "sorted(get_values({'a': 1, 'b': 2}))", expected: "[1, 2]" },
            { input: "{}",               call: "get_values({})",                      expected: "[]" },
          ],
        },
        {
          id: 50,
          title: "Square Root Calculator",
          slug: "python-square-root-calculator-exponentiation",
          seoTitle: "Python Calculate Square Root | Math Operators | Repractiq",
          metaDescription: "Compute the square root of a numeric value in Python using fractional exponentiation or the math module.",
          tags: ["math", "operators"],
          difficulty: "Easy",
          type: "function",
          description: "Write a function called `get_square_root` that takes a number and returns its square root.",
          explanation: "You can calculate a square root by raising a number to the power of 0.5 using the `**` operator, or by importing `math.sqrt()`.",
          hint: "Raise the number to the power of 0.5 using `num ** 0.5`.",
          solutionCode: `def get_square_root(num):\n    return num ** 0.5`,
          expectedOutput: "",
          testCases: [
            { input: "9",  call: "get_square_root(9)",  expected: "3.0" },
            { input: "16", call: "get_square_root(16)", expected: "4.0" },
            { input: "0",  call: "get_square_root(0)",  expected: "0.0" },
          ],
        },
          {
            id: 51,
            title: "Check Even Number",
            slug: "python-check-even-number-boolean",
            seoTitle: "Python Check Even Number | Boolean Modulo Flags | Repractiq",
            metaDescription: "Write a quick evaluation script to verify if an integer is strictly even, returning an explicit Boolean.",
            tags: ["conditionals", "modulo", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `is_even` that takes a number and returns True if it is even, and False otherwise.",
            explanation: "Use the expression `n % 2 == 0` which directly resolves into the desired boolean `True` or `False` result statement.",
            hint: "Return the evaluation result of `n % 2 == 0` directly.",
            solutionCode: `def is_even(n):\n    return n % 2 == 0`,
            expectedOutput: "",
            testCases: [
              { input: "4",  call: "is_even(4)",  expected: "True" },
              { input: "11", call: "is_even(11)", expected: "False" },
            ],
          },
          {
            id: 52,
            title: "Check Odd Number",
            slug: "python-check-odd-number-boolean",
            seoTitle: "Python Check Odd Number | Boolean Modulo Flags | Repractiq",
            metaDescription: "Write a clean evaluation helper checking for numerical oddity parameters returning accurate Booleans.",
            tags: ["conditionals", "modulo", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `is_odd` that takes a number and returns True if it is odd, and False otherwise.",
            explanation: "An integer is odd if dividing it by 2 produces a remainder of 1. Evaluate this cleanly using the expression `n % 2 != 0`.",
            hint: "Return the statement expression evaluation of `n % 2 != 0` directly.",
            solutionCode: `def is_odd(n):\n    return n % 2 != 0`,
            expectedOutput: "",
            testCases: [
              { input: "7",  call: "is_odd(7)",  expected: "True" },
              { input: "10", call: "is_odd(10)", expected: "False" },
            ],
          },
          {
            id: 53,
            title: "Concatenate Strings",
            slug: "python-concatenate-strings-plus-operator",
            seoTitle: "Python Concatenate Strings | Plus Operator | Repractiq",
            metaDescription: "Combine separate text strings together into a unified sequence using Python text addition operations.",
            tags: ["strings", "operators"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `concat_strings` that takes two strings and joins them together with no spacing.",
            explanation: "The addition operator `+` performs string concatenation when applied to string objects, linking them end-to-end.",
            hint: "Use the `+` string operator to add str1 to str2.",
            solutionCode: `def concat_strings(str1, str2):\n    return str1 + str2`,
            expectedOutput: "",
            testCases: [
              { input: "'foo', 'bar'", call: "concat_strings('foo', 'bar')", expected: "foobar" },
              { input: "'a', ''",      call: "concat_strings('a', '')",      expected: "a" },
            ],
          },
          {
            id: 54,
            title: "Last Element of List",
            slug: "python-last-element-of-list-negative-indexing",
            seoTitle: "Python Last Element | Negative Indexing Rules | Repractiq",
            metaDescription: "Access final items inside list sequences efficiently using Python's clean negative indexing bounds.",
            tags: ["lists", "indexing"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `get_last_element` that takes a non-empty list and returns its absolute final item.",
            explanation: "Python offers a clean shorthand called negative indexing. The index position code `-1` targets the final item in a list.",
            hint: "Access index `[-1]` from the input list directly.",
            solutionCode: `def get_last_element(lst):\n    return lst[-1]`,
            expectedOutput: "",
            testCases: [
              { input: "[1, 2, 3]",     call: "get_last_element([1, 2, 3])",     expected: "3" },
              { input: "['x', 'y', 'z']", call: "get_last_element(['x', 'y', 'z'])", expected: "z" },
            ],
          },
          {
            id: 55,
            title: "First Element of List",
            slug: "python-first-element-of-list-indexing",
            seoTitle: "Python First Element | Zero Based Indexing | Repractiq",
            metaDescription: "Access foundational elements sitting at the starting point of standard sequences using zero-based index markers.",
            tags: ["lists", "indexing"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `get_first_element` that takes a non-empty list and returns its very first item.",
            explanation: "Python indices start at zero. Access the starting element of any list by referencing explicit location index `0`.",
            hint: "Access index `[0]` from the input list sequence.",
            solutionCode: `def get_first_element(lst):\n    return lst[0]`,
            expectedOutput: "",
            testCases: [
              { input: "[5, 6, 7]",   call: "get_first_element([5, 6, 7])",   expected: "5" },
              { input: "['a', 'b']", call: "get_first_element(['a', 'b'])", expected: "a" },
            ],
          },
          {
            id: 56,
            title: "String to Integer",
            slug: "python-convert-string-to-integer-int-cast",
            seoTitle: "Python String to Integer | Type Casting Utility | Repractiq",
            metaDescription: "Convert valid numeric string text lines safely into absolute integers using native int() parsing mechanics.",
            tags: ["type conversion", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `string_to_int` that takes a numeric string and converts it into a proper integer number.",
            explanation: "The native class constructor `int()` parses character sequences representing numerical digits into numerical numeric data.",
            hint: "Pass the input value string directly into the `int()` constructor function.",
            solutionCode: `def string_to_int(s):\n    return int(s)`,
            expectedOutput: "",
            testCases: [
              { input: "'123'",  call: "string_to_int('123')",  expected: "123" },
              { input: "'-45'",  call: "string_to_int('-45')",  expected: "-45" },
            ],
          },
          {
            id: 57,
            title: "Integer to String",
            slug: "python-convert-integer-to-string-str-cast",
            seoTitle: "Python Integer to String | Type Casting Utility | Repractiq",
            metaDescription: "Convert active digital numbers back into text character formats using the robust built-in str() method.",
            tags: ["type conversion", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `int_to_string` that takes an integer and converts it into its text string equivalent.",
            explanation: "The native constructor utility `str()` converts numerical inputs neatly back into standard character text string outputs.",
            hint: "Pass the numeric value directly into the `str()` constructor utility function.",
            solutionCode: `def int_to_string(n):\n    return str(n)`,
            expectedOutput: "",
            testCases: [
              { input: "500", call: "int_to_string(500)", expected: "500" },
              { input: "-9",  call: "int_to_string(-9)",  expected: "-9" },
            ],
          },
          {
            id: 58,
            title: "Find Smallest Word",
            slug: "python-find-smallest-word-string",
            seoTitle: "Python Find Smallest Word | String Splitting | Repractiq",
            metaDescription: "Identify the absolute shortest length word isolated inside standard sentence strings using splitting and sorting loops.",
            tags: ["strings", "min", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `find_shortest_word` that takes a sentence string and isolates the shortest word. If tied, return the first one.",
            explanation: "Split the sentence into an independent word list using `.split()`. Then evaluate it using `min(word_list, key=len)`.",
            hint: "Split the string into a list and use the `min()` function with `key=len` parameters passed.",
            solutionCode: `def find_shortest_word(text):\n    words = text.split()\n    return min(words, key=len)`,
            expectedOutput: "",
            testCases: [
              { input: "'I love Python'",     call: "find_shortest_word('I love Python')",     expected: "I" },
              { input: "'cats and big dogs'", call: "find_shortest_word('cats and big dogs')", expected: "and" },
            ],
          },
          {
            id: 59,
            title: "Find Largest Word",
            slug: "python-find-largest-word-string",
            seoTitle: "Python Find Largest Word | String Splitting | Repractiq",
            metaDescription: "Identify the single longest string word resting within complex user sentences using string split mechanics.",
            tags: ["strings", "max", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `find_longest_word` that takes a sentence string and yields the single longest word present.",
            explanation: "Break text loops up into words using `.split()`. Run a maximum value search rule against lengths using `max(words, key=len)`.",
            hint: "Split the paragraph text line down using `.split()`, then find the max value using `key=len` configurations.",
            solutionCode: `def find_longest_word(text):\n    words = text.split()\n    return max(words, key=len)`,
            expectedOutput: "",
            testCases: [
              { input: "'Coding is spectacular'", call: "find_longest_word('Coding is spectacular')", expected: "spectacular" },
              { input: "'a b c intermediate'",   call: "find_longest_word('a b c intermediate')",   expected: "intermediate" },
            ],
          },
          {
            id: 60,
            title: "Calculate Perimeter of Square",
            slug: "python-calculate-perimeter-square",
            seoTitle: "Python Perimeter of Square | Arithmetic Math | Repractiq",
            metaDescription: "Compute the boundary total outer perimeter lengths of square shapes in Python using elementary arithmetic loops.",
            tags: ["math", "arithmetic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `square_perimeter` that takes the side length of a square and returns its outer perimeter value.",
            explanation: "A square features 4 identical boundaries. Multiplying the value of one boundary parameter length by 4 yields the accurate outer perimeter summary.",
            hint: "Multiply the designated side parameter input value explicitly by 4.",
            solutionCode: `def square_perimeter(side):\n    return side * 4`,
            expectedOutput: "",
            testCases: [
              { input: "5",  call: "square_perimeter(5)",  expected: "20" },
              { input: "1.5", call: "square_perimeter(1.5)", expected: "6.0" },
            ],
          },
          {
            id: 61,
            title: "Calculate Area of Rectangle",
            slug: "python-calculate-area-rectangle",
            seoTitle: "Python Area of Rectangle | Mathematical Multiplications | Repractiq",
            metaDescription: "Compute spaces trapped within classic rectangle forms using foundational mathematical dimension formulas.",
            tags: ["math", "arithmetic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `rectangle_area` that takes length and width arguments and outputs calculated area metrics.",
            explanation: "Multiplying width by structural height configurations scales coordinate grids cleanly into absolute base metrics.",
            hint: "Return length multiplied by the width parameters.",
            solutionCode: `def rectangle_area(length, width):\n    return length * width`,
            expectedOutput: "",
            testCases: [
              { input: "5, 4",  call: "rectangle_area(5, 4)",  expected: "20" },
              { input: "3, 2.5", call: "rectangle_area(3, 2.5)", expected: "7.5" },
            ],
          },
          {
            id: 62,
            title: "Exclusive List Comprehension Filter",
            slug: "python-list-comprehension-greater-than-five",
            seoTitle: "Python List Comprehension | Conditional Data Filters | Repractiq",
            metaDescription: "Filter out target numeric groups inside raw arrays instantly matching precise conditional list comprehension syntax maps.",
            tags: ["lists", "list comprehension", "filter"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `filter_greater_than_five` that selects numbers higher than 5 using inline list comprehensions.",
            explanation: "Using syntax structures like `[val for val in array if val > 5]` automatically generates conditional slices safely.",
            hint: "Use inline conditions like `if x > 5` trailing inside standard list comprehension brackets.",
            solutionCode: `def filter_greater_than_five(numbers):\n    return [x for x in numbers if x > 5]`,
            expectedOutput: "",
            testCases: [
              { input: "[3, 5, 8, 10, 2]", call: "str(filter_greater_than_five([3, 5, 8, 10, 2]))", expected: "[8, 10]" },
              { input: "[1, 2, 4]",        call: "str(filter_greater_than_five([1, 2, 4]))",        expected: "[]" },
            ],
          },
          {
            id: 63,
            title: "Count Dictionary Key Length",
            slug: "python-count-dictionary-keys-total",
            seoTitle: "Python Total Dictionary Keys | Len Mapping Tricks | Repractiq",
            metaDescription: "Count the total number of keys in a Python dictionary map layout safely using basic len mapping utilities.",
            tags: ["dictionaries", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `count_keys` that calculates the total key fields currently configured in a target dictionary map.",
            explanation: "Running `len(dictionary)` automatically returns the base layout keys count accurately without loops.",
            hint: "Pass the dictionary container object straight inside standard `len()` methods.",
            solutionCode: `def count_keys(d):\n    return len(d)`,
            expectedOutput: "",
            testCases: [
              { input: "{'x': 1, 'y': 2, 'z': 3}", call: "count_keys({'x': 1, 'y': 2, 'z': 3})", expected: "3" },
              { input: "{}",                        call: "count_keys({})",                        expected: "0" },
            ],
          },
          {
            id: 64,
            title: "Float to Integer Conversion",
            slug: "python-convert-float-to-integer-truncation",
            seoTitle: "Python Float to Integer | Numeric Truncation Casting | Repractiq",
            metaDescription: "Convert decimal floating numbers directly into integer types by stripping decimal positions using int casting rules.",
            tags: ["type conversion", "math"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `float_to_int` that takes a floating point decimal input and truncates it down to a simple integer value.",
            explanation: "Wrapping values within the `int()` constructor drops fractions entirely, pulling coordinates down to absolute numbers.",
            hint: "Cast values inside the `int()` type function to strip away trailing decimal parts.",
            solutionCode: `def float_to_int(f):\n    return int(f)`,
            expectedOutput: "",
            testCases: [
              { input: "5.99",  call: "float_to_int(5.99)",  expected: "5" },
              { input: "-2.34", call: "float_to_int(-2.34)", expected: "-2" },
            ],
          },
          {
            id: 65,
            title: "Integer to Float Conversion",
            slug: "python-convert-integer-to-float-casting",
            seoTitle: "Python Integer to Float | Decimal Conversion Casting | Repractiq",
            metaDescription: "Add explicit floating point fractions to whole base integers using standard float type conversion casting blocks.",
            tags: ["type conversion", "math"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `int_to_float` that converts a whole integer number directly into a decimal float variable format.",
            explanation: "The `float()` type constructor converts whole integers into formal floating point decimal numbers.",
            hint: "Wrap structural integers inside the native `float()` conversion constructor method.",
            solutionCode: `def int_to_float(n):\n    return float(n)`,
            expectedOutput: "",
            testCases: [
              { input: "4",   call: "int_to_float(4)",   expected: "4.0" },
              { input: "-10", call: "int_to_float(-10)", expected: "-10.0" },
            ],
          },
          {
            id: 66,
            title: "String Multiplication Repeater",
            slug: "python-string-multiplication-repeater",
            seoTitle: "Python String Repeater | Arithmetic Operator Replications | Repractiq",
            metaDescription: "Repeat phrase text sequences out onto string line grids cleanly using math multiplication operators.",
            tags: ["strings", "operators"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `repeat_word` that accepts a string segment and returns it duplicated exactly two times consecutively with no separation spacing.",
            explanation: "Using the multiplication operator syntax expressions like `word * 2` instantly links strings uniformly.",
            hint: "Multiply your word parameter explicitly by `2`.",
            solutionCode: `def repeat_word(word):\n    return word * 2`,
            expectedOutput: "",
            testCases: [
              { input: "'echo'",  call: "repeat_word('echo')",  expected: "echoecho" },
              { input: "'test'",  call: "repeat_word('test')",  expected: "testtest" },
            ],
          },
          {
            id: 67,
            title: "Check Vowel Existence",
            slug: "python-check-vowel-existence-membership",
            seoTitle: "Python Check Vowel Presence | String Membership In | Repractiq",
            metaDescription: "Test characters to confirm if they match specific vowel definitions using standard set search patterns.",
            tags: ["strings", "conditionals", "membership"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `is_vowel` that takes a single character string and flags if it represents a valid vowel (a, e, i, o, u). Handle upper and lower cases.",
            explanation: "Convert individual incoming string inputs using `.lower()` and check the collection array string bounds using the `in` operator.",
            hint: "Check if your input variable lowered falls inside target matching collections like `'aeiou'`.",
            solutionCode: `def is_vowel(char):\n    return char.lower() in "aeiou"`,
            expectedOutput: "",
            testCases: [
              { input: "'a'", call: "is_vowel('a')", expected: "True" },
              { input: "'Z'", call: "is_vowel('Z')", expected: "False" },
              { input: "'E'", call: "is_vowel('E')", expected: "True" },
            ],
          },
          {
            id: 68,
            title: "Calculate Cube of Number",
            slug: "python-calculate-cube-of-number",
            seoTitle: "Python Calculate Cube | Exponential Power Multipliers | Repractiq",
            metaDescription: "Calculate the cubic numeric sum scale limits of arguments easily using basic exponential logic syntax maps.",
            tags: ["math", "operators"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `cube_number` that computes the math cubic capacity boundaries (raised to the third power) of basic inputs.",
            explanation: "The syntax operator `**` scales numbers up into exponential dimensions cleanly. Apply `num ** 3` to resolve values instantly.",
            hint: "Raise numerical metrics explicitly to the power of 3 utilizing structural `** 3` operators.",
            solutionCode: `def cube_number(num):\n    return num ** 3`,
            expectedOutput: "",
            testCases: [
              { input: "3",  call: "cube_number(3)",  expected: "27" },
              { input: "-2", call: "cube_number(-2)", expected: "-8" },
              { input: "0",  call: "cube_number(0)",  expected: "0" },
            ],
          },
          {
            id: 69,
            title: "Verify Dictionary Value Presence",
            slug: "python-verify-dictionary-value-membership",
            seoTitle: "Python Check Dictionary Value | Value View Membership | Repractiq",
            metaDescription: "Determine if specific values exist buried deep within tracking maps using explicit dictionary values search layouts.",
            tags: ["dictionaries", "membership"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `value_exists` that returns True if a targeted element matches any active value defined within a given dictionary.",
            explanation: "Call the `.values()` view mapping from target dictionaries, then apply search membership checks using the `in` syntax operator.",
            hint: "Write validation loops targeting parameters matching `target in d.values()` expressions.",
            solutionCode: `def value_exists(d, target):\n    return target in d.values()`,
            expectedOutput: "",
            testCases: [
              { input: "{'a': 10, 'b': 20}, 20", call: "value_exists({'a': 10, 'b': 20}, 20)", expected: "True" },
              { input: "{'a': 10, 'b': 20}, 99", call: "value_exists({'a': 10, 'b': 20}, 99)", expected: "False" },
            ],
          },
          {
            id: 70,
            title: "Split String into Words",
            slug: "python-split-string-into-words-split",
            seoTitle: "Python Split String | Word Array Tokenization | Repractiq",
            metaDescription: "Tokenize long spacing strings up into clean individual item arrays matching default string split routines.",
            tags: ["strings", "lists"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `split_words` that converts a multi-word spacing sentence down into an ordered list of isolated words.",
            explanation: "Invoking default string object methods like `.split()` automatically slices character sections along spacing coordinates.",
            hint: "Call structural string operations like `text.split()` directly.",
            solutionCode: `def split_words(text):\n    return text.split()`,
            expectedOutput: "",
            testCases: [
              { input: "'hello world'", call: "str(split_words('hello world'))", expected: "['hello', 'world']" },
              { input: "'Python'",     call: "str(split_words('Python'))",     expected: "['Python']" },
            ],
          },
          {
            id: 71,
            title: "Celsius to Fahrenheit Converter",
            slug: "python-celsius-to-fahrenheit-converter",
            seoTitle: "Python Celsius to Fahrenheit | Math Formulas | Repractiq",
            metaDescription: "Convert temperatures from Celsius to Fahrenheit using basic arithmetic operations in Python.",
            tags: ["math", "arithmetic", "conversions"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `celsius_to_fahrenheit` that takes a temperature in Celsius and returns its equivalent value in Fahrenheit.",
            explanation: "The conversion formula is (Celsius * 9/5) + 32. Apply this formula directly to find the Fahrenheit equivalent.",
            hint: "Multiply the Celsius temperature by 9/5 and then add 32.",
            solutionCode: `def celsius_to_fahrenheit(celsius):\n    return (celsius * 9 / 5) + 32`,
            expectedOutput: "",
            testCases: [
              { input: "0",   call: "celsius_to_fahrenheit(0)",   expected: "32.0" },
              { input: "100", call: "celsius_to_fahrenheit(100)", expected: "212.0" },
              { input: "-40", call: "celsius_to_fahrenheit(-40)", expected: "-40.0" },
            ],
          },
          {
            id: 72,
            title: "Find the Average of a List",
            slug: "python-find-average-of-list",
            seoTitle: "Python List Average | Sum and Len Utilities | Repractiq",
            metaDescription: "Calculate the mathematical average score of numerical elements inside a list using Python sum and len.",
            tags: ["lists", "math", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `find_average` that accepts a non-empty list of numbers and returns their mathematical average.",
            explanation: "Compute the average by dividing the total sum of all list elements (`sum(lst)`) by the total count of those elements (`len(lst)`).",
            hint: "Divide `sum(numbers)` by `len(numbers)`.",
            solutionCode: `def find_average(numbers):\n    return sum(numbers) / len(numbers)`,
            expectedOutput: "",
            testCases: [
              { input: "[1, 2, 3, 4, 5]", call: "find_average([1, 2, 3, 4, 5])", expected: "3.0" },
              { input: "[10, 20]",       call: "find_average([10, 20])",       expected: "15.0" },
            ],
          },
          {
            id: 73,
            title: "String Starts With Checker",
            slug: "python-string-starts-with-prefix",
            seoTitle: "Python String Starts With | Prefix Verification | Repractiq",
            metaDescription: "Verify whether a string begins with a specified prefix using Python's native startswith method.",
            tags: ["strings", "built-ins", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `starts_with` that takes a string and a prefix, returning True if the string starts with that prefix, and False otherwise.",
            explanation: "The built-in string method `.startswith(prefix)` yields a direct boolean assessment evaluating a string's starting boundary.",
            hint: "Use the `text.startswith(prefix)` method.",
            solutionCode: `def starts_with(text, prefix):\n    return text.startswith(prefix)`,
            expectedOutput: "",
            testCases: [
              { input: "'hello world', 'hell'", call: "starts_with('hello world', 'hell')", expected: "True" },
              { input: "'python', 'java'",       call: "starts_with('python', 'java')",       expected: "False" },
            ],
          },
          {
            id: 74,
            title: "String Ends With Checker",
            slug: "python-string-ends-with-suffix",
            seoTitle: "Python String Ends With | Suffix Verification | Repractiq",
            metaDescription: "Verify whether a string finishes with a specified suffix using Python's native endswith method.",
            tags: ["strings", "built-ins", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `ends_with` that takes a string and a suffix, returning True if the string ends with that suffix, and False otherwise.",
            explanation: "The built-in string method `.endswith(suffix)` directly validates the trailing edge characters of a text segment.",
            hint: "Use the `text.endswith(suffix)` method.",
            solutionCode: `def ends_with(text, suffix):\n    return text.endswith(suffix)`,
            expectedOutput: "",
            testCases: [
              { input: "'format.txt', '.txt'", call: "ends_with('format.txt', '.txt')", expected: "True" },
              { input: "'coding', 'txt'",      call: "ends_with('coding', 'txt')",      expected: "False" },
            ],
          },
          {
            id: 75,
            title: "Filter Odd Numbers",
            slug: "python-filter-odd-numbers-list-comprehension",
            seoTitle: "Python Filter Odd Numbers | List Comprehension | Repractiq",
            metaDescription: "Filter odd numbers out of a numeric list using optimized Python conditional list comprehensions.",
            tags: ["lists", "list comprehension", "filter"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `filter_odds` that takes a list of numbers and returns a new list containing only the odd values.",
            explanation: "An inline list comprehension like `[x for x in numbers if x % 2 != 0]` neatly discards even values.",
            hint: "Use a list comprehension with a condition checking if `x % 2 != 0`.",
            solutionCode: `def filter_odds(numbers):\n    return [x for x in numbers if x % 2 != 0]`,
            expectedOutput: "",
            testCases: [
              { input: "[1, 2, 3, 4, 5]", call: "str(filter_odds([1, 2, 3, 4, 5]))", expected: "[1, 3, 5]" },
              { input: "[2, 4, 6]",       call: "str(filter_odds([2, 4, 6]))",       expected: "[]" },
            ],
          },
          {
            id: 76,
            title: "Merge Two Dictionaries",
            slug: "python-merge-two-dictionaries",
            seoTitle: "Python Merge Dictionaries | Dictionary Operations | Repractiq",
            metaDescription: "Combine key-value pairs from two Python dictionaries into a single tracking collection map.",
            tags: ["dictionaries", "operators"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `merge_dicts` that takes two dictionaries and returns a new dictionary combining both. If keys overlap, the second dictionary's value should take precedence.",
            explanation: "In modern Python, you can unpack keys and values from dictionaries using the double asterisk operator `**` or use the `|` merge operator.",
            hint: "You can combine them using `{**d1, **d2}` or the `|` operator in newer versions.",
            solutionCode: `def merge_dicts(d1, d2):\n    return {**d1, **d2}`,
            expectedOutput: "",
            testCases: [
              { input: "{'a': 1}, {'b': 2}",     call: "str(sorted(merge_dicts({'a': 1}, {'b': 2}).items()))", expected: "[('a', 1), ('b', 2)]" },
              { input: "{'x': 5}, {'x': 10, 'y': 2}", call: "str(sorted(merge_dicts({'x': 5}, {'x': 10, 'y': 2}).items()))", expected: "[('x', 10), ('y', 2)]" },
            ],
          },
          {
            id: 77,
            title: "Generate Range List",
            slug: "python-generate-range-list-constructor",
            seoTitle: "Python Generate Range List | Range Constructor | Repractiq",
            metaDescription: "Generate a list containing structured integers from a starting point up to an exclusive ceiling using range.",
            tags: ["lists", "range", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `generate_range` that accepts a start integer and an exclusive end integer, returning a list of all numbers within that span.",
            explanation: "Passing limits to the `range(start, end)` constructor creates an iterator, which you can easily cast into a standard structural list using `list()`.",
            hint: "Convert the `range(start, end)` output object directly into a `list()`.",
            solutionCode: `def generate_range(start, end):\n    return list(range(start, end))`,
            expectedOutput: "",
            testCases: [
              { input: "1, 5",  call: "str(generate_range(1, 5))",  expected: "[1, 2, 3, 4]" },
              { input: "10, 13", call: "str(generate_range(10, 13))", expected: "[10, 11, 12]" },
            ],
          },
          {
            id: 78,
            title: "Check Substring Existence",
            slug: "python-check-substring-existence-in-operator",
            seoTitle: "Python Check Substring Presence | Membership Operator | Repractiq",
            metaDescription: "Detect if a shorter text fragment resides inside a larger body string using the native in keyword.",
            tags: ["strings", "membership", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `contains_substring` that checks if a sub-segment string exists inside a target text string.",
            explanation: "The Python `in` operator checks string boundaries efficiently, resolving directly to True or False.",
            hint: "Evaluate `sub in main_string` directly.",
            solutionCode: `def contains_substring(main_string, sub):\n    return sub in main_string`,
            expectedOutput: "",
            testCases: [
              { input: "'learning python', 'python'", call: "contains_substring('learning python', 'python')", expected: "True" },
              { input: "'javascript', 'python'",      call: "contains_substring('javascript', 'python')",      expected: "False" },
            ],
          },
          {
            id: 79,
            title: "Count Capital Letters",
            slug: "python-count-capital-letters-isupper",
            seoTitle: "Python Count Capital Letters | Character Evaluations | Repractiq",
            metaDescription: "Count the number of uppercase uppercase letters inside mixed text strings using string loops.",
            tags: ["strings", "loops", "conditionals"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `count_uppercase` that determines the total number of uppercase uppercase characters sitting within a target string.",
            explanation: "Loop through each character in the string, using the `.isupper()` status filter check to track and sum up instances.",
            hint: "Check character state flags with `char.isupper()` while looping.",
            solutionCode: `def count_uppercase(s):\n    return sum(1 for char in s if char.isupper())`,
            expectedOutput: "",
            testCases: [
              { input: "'PyThOn'",    call: "count_uppercase('PyThOn')",    expected: "3" },
              { input: "'hello code'", call: "count_uppercase('hello code')", expected: "0" },
            ],
          },
          {
            id: 80,
            title: "Absolute Value Calculator",
            slug: "python-absolute-value-calculator-abs",
            seoTitle: "Python Absolute Value | Built-in abs() | Repractiq",
            metaDescription: "Compute the non-negative distance of real integer values using Python's native absolute utility.",
            tags: ["math", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `get_absolute_value` that calculates the distance of a numerical value from zero.",
            explanation: "The built-in function `abs(n)` automatically converts negative magnitudes into positive values while preserving zero and positive metrics.",
            hint: "Pass the input number directly into the native `abs()` utility function.",
            solutionCode: `def get_absolute_value(n):\n    return abs(n)`,
            expectedOutput: "",
            testCases: [
              { input: "-15", call: "get_absolute_value(-15)", expected: "15" },
              { input: "7",   call: "get_absolute_value(7)",   expected: "7" },
              { input: "0",   call: "get_absolute_value(0)",   expected: "0" },
            ],
          },
          {
            id: 81,
            title: "List Element Multiplier",
            slug: "python-list-element-multiplier-loop",
            seoTitle: "Python List Element Multiplier | Product Loops | Repractiq",
            metaDescription: "Multiply all numbers inside a given list step-by-step using iteration loops to compute the final product.",
            tags: ["lists", "loops", "math"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `multiply_list` that processes a list of numbers and returns the product of all its elements combined.",
            explanation: "Initialize a product tracker at 1, then loop through the list, multiplying the tracker by each value successively.",
            hint: "Start a tracking variable at 1, then multiply it by each number in a loop.",
            solutionCode: `def multiply_list(numbers):\n    product = 1\n    for num in numbers:\n        product *= num\n    return product`,
            expectedOutput: "",
            testCases: [
              { input: "[1, 2, 3, 4]", call: "multiply_list([1, 2, 3, 4])", expected: "24" },
              { input: "[5, 0, 10]",   call: "multiply_list([5, 0, 10])",   expected: "0" },
            ],
          },
          {
            id: 82,
            title: "Find Key with Maximum Value",
            slug: "python-find-key-maximum-value-dictionary",
            seoTitle: "Python Find Max Dictionary Key | Key Extractions | Repractiq",
            metaDescription: "Extract the dictionary key that maps to the highest numerical value using custom max parameters.",
            tags: ["dictionaries", "max", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `max_value_key` that identifies and returns the key corresponding to the largest value in a non-empty dictionary.",
            explanation: "Pass the dictionary itself to the `max()` function, using `d.get` as the evaluation key parameter to extract the key with the highest value.",
            hint: "Use `max(d, key=d.get)` to query keys using value properties.",
            solutionCode: `def max_value_key(d):\n    return max(d, key=d.get)`,
            expectedOutput: "",
            testCases: [
              { input: "{'a': 10, 'b': 50, 'c': 30}", call: "max_value_key({'a': 10, 'b': 50, 'c': 30})", expected: "b" },
              { input: "{'apple': 3, 'pear': 1}",     call: "max_value_key({'apple': 3, 'pear': 1})",     expected: "apple" },
            ],
          },
          {
            id: 83,
            title: "String Character Replacer",
            slug: "python-string-character-replacer-replace",
            seoTitle: "Python String Character Replacer | replace Method | Repractiq",
            metaDescription: "Swap out specific character substrings within text sequences using Python's native string replace mechanism.",
            tags: ["strings", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `replace_char` that takes a target string, an old character segment, and a new character segment, and returns the modified string.",
            explanation: "The native string method `text.replace(old, new)` searches the target string and replaces all instances of the old segment.",
            hint: "Call `text.replace(old, new)` on the target string.",
            solutionCode: `def replace_char(text, old, new):\n    return text.replace(old, new)`,
            expectedOutput: "",
            testCases: [
              { input: "'banana', 'a', 'o'", call: "replace_char('banana', 'a', 'o')", expected: "bonono" },
              { input: "'cats', 'z', 'x'",   call: "replace_char('cats', 'z', 'x')",   expected: "cats" },
            ],
          },
          {
            id: 84,
            title: "Check for Dictionary Key",
            slug: "python-check-dictionary-key-membership",
            seoTitle: "Python Check Dictionary Key | Key Search Logic | Repractiq",
            metaDescription: "Determine whether a specific key exists inside a tracking dictionary map layout using the in operator.",
            tags: ["dictionaries", "membership", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `key_exists` that returns True if a given key is present in a dictionary, and False otherwise.",
            explanation: "Using `key in dictionary` directly performs an optimized evaluation of the dictionary's keys.",
            hint: "Use the `in` operator to search the dictionary keys directly.",
            solutionCode: `def key_exists(d, key):\n    return key in d`,
            expectedOutput: "",
            testCases: [
              { input: "{'id': 1, 'name': 'Bob'}, 'name'", call: "key_exists({'id': 1, 'name': 'Bob'}, 'name')", expected: "True" },
              { input: "{'id': 1}, 'age'",                  call: "key_exists({'id': 1}, 'age')",                  expected: "False" },
            ],
          },
          {
            id: 85,
            title: "List Element Swapper",
            slug: "python-list-element-swapper-indices",
            seoTitle: "Python List Element Swapper | Tuple Assignments | Repractiq",
            metaDescription: "Swap positions of elements within an array based on two index coordinates using tuple unpacking assignment rules.",
            tags: ["lists", "indexing"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `swap_elements` that takes a list and two indices, swaps the elements at those positions, and returns the modified list.",
            explanation: "Python allows parallel value assignment via tuple unpacking: `lst[i1], lst[i2] = lst[i2], lst[i1]`.",
            hint: "Use simultaneous assignment `lst[i1], lst[i2] = lst[i2], lst[i1]` to swap values.",
            solutionCode: `def swap_elements(lst, idx1, idx2):\n    lst[idx1], lst[idx2] = lst[idx2], lst[idx1]\n    return lst`,
            expectedOutput: "",
            testCases: [
              { input: "[10, 20, 30, 40], 1, 3", call: "str(swap_elements([10, 20, 30, 40], 1, 3))", expected: "[10, 40, 30, 20]" },
              { input: "['a', 'b'], 0, 1",       call: "str(swap_elements(['a', 'b'], 0, 1))",       expected: "['b', 'a']" },
            ],
          },
          {
            id: 86,
            title: "Leap Year Identifier",
            slug: "python-leap-year-identifier-conditionals",
            seoTitle: "Python Leap Year Identifier | Modulo Evaluation Logic | Repractiq",
            metaDescription: "Determine whether a given calendar year is a leap year based on classic standard modulo alignment criteria.",
            tags: ["conditionals", "modulo", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `is_leap_year` that takes a year integer and returns True if it is a leap year, and False otherwise.",
            explanation: "A year is a leap year if it is divisible by 4, except for end-of-century years, which must also be divisible by 400.",
            hint: "A year is a leap year if `year % 4 == 0` and (`year % 100 != 0` or `year % 400 == 0`).",
            solutionCode: `def is_leap_year(year):\n    return year % 4 == 0 and (year % 100 != 0 or year % 400 == 0)`,
            expectedOutput: "",
            testCases: [
              { input: "2020", call: "is_leap_year(2020)", expected: "True" },
              { input: "1900", call: "is_leap_year(1900)", expected: "False" },
              { input: "2000", call: "is_leap_year(2000)", expected: "True" },
            ],
          },
          {
            id: 87,
            title: "Calculate Triangle Area",
            slug: "python-calculate-triangle-area-formula",
            seoTitle: "Python Triangle Area | Arithmetic Layout Formulas | Repractiq",
            metaDescription: "Calculate the surface area of a triangle given its base and height dimension parameters using standard math.",
            tags: ["math", "arithmetic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `triangle_area` that takes base and height arguments and returns the triangle's calculated area.",
            explanation: "The area of a triangle is computed using the formula: `0.5 * base * height`.",
            hint: "Multiply base by height, then divide the result by 2.",
            solutionCode: `def triangle_area(base, height):\n    return 0.5 * base * height`,
            expectedOutput: "",
            testCases: [
              { input: "10, 5", call: "triangle_area(10, 5)", expected: "25.0" },
              { input: "6, 3.5", call: "triangle_area(6, 3.5)", expected: "10.5" },
            ],
          },
          {
            id: 88,
            title: "Extract Dictionary Keys as List",
            slug: "python-get-dictionary-keys-list",
            seoTitle: "Python Get Dictionary Keys | Keys View Conversions | Repractiq",
            metaDescription: "Extract all keys from a dictionary mapping container and collect them as a standard sequential list.",
            tags: ["dictionaries", "lists"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `get_keys` that accepts a dictionary and returns a list containing all its keys.",
            explanation: "Access the dictionary's keys using the `.keys()` view method, and cast it into a standard list using the `list()` constructor.",
            hint: "Pass `d.keys()` into the `list()` constructor function.",
            solutionCode: `def get_keys(d):\n    return list(d.keys())`,
            expectedOutput: "",
            testCases: [
              { input: "{'x': 1, 'y': 2}", call: "str(sorted(get_keys({'x': 1, 'y': 2})))", expected: "['x', 'y']" },
              { input: "{}",               call: "str(get_keys({}))",                      expected: "[]" },
            ],
          },
          {
            id: 89,
            title: "Flatten a List of Lists",
            slug: "python-flatten-list-of-lists-comprehension",
            seoTitle: "Python Flatten Nested Lists | Nested List Comprehension | Repractiq",
            metaDescription: "Flatten a 2D list into a single flat 1D list sequence using nested list comprehension loops.",
            tags: ["lists", "list comprehension"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `flatten_list` that takes a list of lists and flattens it into a single one-dimensional list.",
            explanation: "A nested list comprehension like `[item for sublist in matrix for item in sublist]` iterates through the inner lists and collects their elements.",
            hint: "Use a nested list comprehension: `[item for sublist in lst for item in sublist]`.",
            solutionCode: `def flatten_list(nested_list):\n    return [item for sublist in nested_list for item in sublist]`,
            expectedOutput: "",
            testCases: [
              { input: "[[1, 2], [3, 4]]", call: "str(flatten_list([[1, 2], [3, 4]]))", expected: "[1, 2, 3, 4]" },
              { input: "[['a'], ['b']]",   call: "str(flatten_list([['a'], ['b']]))",   expected: "['a', 'b']" },
            ],
          },
          {
            id: 90,
            title: "Convert String to Float",
            slug: "python-convert-string-to-float-casting",
            seoTitle: "Python String to Float | Type Casting Mechanics | Repractiq",
            metaDescription: "Convert valid decimal text strings into float numbers using standard type casting rules.",
            tags: ["type conversion", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `string_to_float` that converts a decimal string into a floating-point number.",
            explanation: "The native class constructor `float(s)` parses character strings representing numerical decimals into actual float values.",
            hint: "Pass the input string directly into the native `float()` constructor function.",
            solutionCode: `def string_to_float(s):\n    return float(s)`,
            expectedOutput: "",
            testCases: [
              { input: "'3.14'",  call: "string_to_float('3.14')",  expected: "3.14" },
              { input: "'-0.5'",  call: "string_to_float('-0.5')",  expected: "-0.5" },
            ],
          },
          {
            id: 91,
            title: "Remove Even Numbers from List",
            slug: "python-remove-evens-list-comprehension",
            seoTitle: "Python Remove Even Numbers | List Comprehension Filters | Repractiq",
            metaDescription: "Filter out even numbers from a list using list comprehensions to keep only the odd elements.",
            tags: ["lists", "list comprehension", "filter"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `remove_evens` that removes all even integers from a list, returning the remaining odd elements.",
            explanation: "Using the condition `if val % 2 != 0` within a list comprehension filters out elements divisible by 2.",
            hint: "Build a list comprehension that checks if `x % 2 != 0`.",
            solutionCode: `def remove_evens(lst):\n    return [x for x in lst if x % 2 != 0]`,
            expectedOutput: "",
            testCases: [
              { input: "[1, 2, 3, 4, 6]", call: "str(remove_evens([1, 2, 3, 4, 6]))", expected: "[1, 3]" },
              { input: "[2, 4, 8]",       call: "str(remove_evens([2, 4, 8]))",       expected: "[]" },
            ],
          },
          {
            id: 92,
            title: "Calculate Cube Root",
            slug: "python-calculate-cube-root-exponentiation",
            seoTitle: "Python Calculate Cube Root | Fractional Exponents | Repractiq",
            metaDescription: "Compute the cube root of numerical values using fractional power exponentiation syntax maps.",
            tags: ["math", "operators"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `get_cube_root` that takes a positive number and calculates its mathematical cube root.",
            explanation: "You can find the cube root of a number by raising it to the power of `1/3` (or approximately `0.3333333333333333`) using the `**` operator.",
            hint: "Raise the target value to the fractional power of `(1/3)` using `num ** (1/3)`.",
            solutionCode: `def get_cube_root(num):\n    return num ** (1/3)`,
            expectedOutput: "",
            testCases: [
              { input: "27", call: "round(get_cube_root(27), 2)", expected: "3.0" },
              { input: "8",  call: "round(get_cube_root(8), 2)",  expected: "2.0" },
            ],
          },
          {
            id: 93,
            title: "List Element Inversion Filter",
            slug: "python-list-element-inversion-negatives",
            seoTitle: "Python Invert Number Signs | List Comprehension loops | Repractiq",
            metaDescription: "Invert the mathematical sign of every number in a list using inline list comprehensions.",
            tags: ["lists", "list comprehension", "math"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `invert_signs` that takes a list of numbers and returns a new list with the sign of each element inverted.",
            explanation: "An inline comprehension like `[-x for x in numbers]` flips positive numbers to negative and vice versa.",
            hint: "Use an inline list comprehension that prepends a negative sign to each item: `-x`.",
            solutionCode: `def invert_signs(numbers):\n    return [-x for x in numbers]`,
            expectedOutput: "",
            testCases: [
              { input: "[1, -2, 3]", call: "str(invert_signs([1, -2, 3]))", expected: "[-1, 2, -3]" },
              { input: "[0, 5]",     call: "str(invert_signs([0, 5]))",     expected: "[0, -5]" },
            ],
          },
          {
            id: 94,
            title: "Count Specific Character in String",
            slug: "python-count-character-occurrences-string",
            seoTitle: "Python Count Character Occurrences | String count Method | Repractiq",
            metaDescription: "Count how many times a specific character appears in a string using Python's native count method.",
            tags: ["strings", "counting", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `count_char` that takes a string and a target character, and returns the number of times the character appears in the string.",
            explanation: "The built-in string method `text.count(char)` counts non-overlapping occurrences of the target character.",
            hint: "Use the built-in `text.count(char)` method.",
            solutionCode: `def count_char(text, char):\n    return text.count(char)`,
            expectedOutput: "",
            testCases: [
              { input: "'apple', 'p'",  call: "count_char('apple', 'p')",  expected: "2" },
              { input: "'hello', 'z'",  call: "count_char('hello', 'z')",  expected: "0" },
            ],
          },
          {
            id: 95,
            title: "Find the Maximum of Three Numbers",
            slug: "python-find-maximum-of-three-numbers",
            seoTitle: "Python Max of Three Numbers | Built-in max Utility | Repractiq",
            metaDescription: "Find the largest of three numbers using Python's native max utility function.",
            tags: ["math", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `max_of_three` that takes three numbers as arguments and returns the largest one.",
            explanation: "The built-in `max()` function can accept multiple arguments directly and returns the maximum value among them.",
            hint: "Pass all three arguments directly into the native `max(a, b, c)` function.",
            solutionCode: `def max_of_three(a, b, c):\n    return max(a, b, c)`,
            expectedOutput: "",
            testCases: [
              { input: "1, 5, 3",    call: "max_of_three(1, 5, 3)",    expected: "5" },
              { input: "10, 2, -4",  call: "max_of_three(10, 2, -4)",  expected: "10" },
            ],
          },
          {
            id: 96,
            title: "Check for All Equal Elements",
            slug: "python-check-all-elements-equal-list",
            seoTitle: "Python Check All Elements Equal | Set Unique Reductions | Repractiq",
            metaDescription: "Check if all elements in a list are identical using Python sets to isolate unique elements.",
            tags: ["lists", "sets", "logic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `all_equal` that takes a list and returns True if all elements are identical, and False otherwise.",
            explanation: "Converting the list to a `set()` eliminates duplicate values. If all elements are identical, the length of the resulting set will be 1 (or 0 if the list is empty).",
            hint: "Convert the list to a set and check if `len(set(lst)) <= 1`.",
            solutionCode: `def all_equal(lst):\n    return len(set(lst)) <= 1`,
            expectedOutput: "",
            testCases: [
              { input: "[7, 7, 7]",    call: "all_equal([7, 7, 7])",    expected: "True" },
              { input: "[1, 2, 1]",    call: "all_equal([1, 2, 1])",    expected: "False" },
              { input: "[]",           call: "all_equal([])",           expected: "True" },
            ],
          },
          {
            id: 97,
            title: "Convert List of Strings to Upper",
            slug: "python-convert-list-strings-to-upper-comprehension",
            seoTitle: "Python List Strings Upper | List Comprehensions | Repractiq",
            metaDescription: "Convert a list of strings to uppercase using Python list comprehensions.",
            tags: ["lists", "strings", "list comprehension"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `uppercase_strings` that takes a list of strings and returns a new list with each string converted to uppercase.",
            explanation: "Use a list comprehension like `[s.upper() for s in words]` to apply the `.upper()` method to each string in the list.",
            hint: "Use a list comprehension that calls `.upper()` on each item.",
            solutionCode: `def uppercase_strings(words):\n    return [s.upper() for s in words]`,
            expectedOutput: "",
            testCases: [
              { input: "['a', 'b']",       call: "str(uppercase_strings(['a', 'b']))",       expected: "['A', 'B']" },
              { input: "['hello', 'xyz']", call: "str(uppercase_strings(['hello', 'xyz']))", expected: "['HELLO', 'XYZ']" },
            ],
          },
          {
            id: 98,
            title: "Sum of Dictionary Values",
            slug: "python-sum-dictionary-values-total",
            seoTitle: "Python Sum Dictionary Values | Value View Sums | Repractiq",
            metaDescription: "Calculate the total sum of all numerical values in a dictionary using Python's sum function.",
            tags: ["dictionaries", "math", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `sum_dict_values` that calculates the total sum of all numerical values stored inside a dictionary.",
            explanation: "Extract the values view using `d.values()`, then pass them directly into the native `sum()` function to calculate the total.",
            hint: "Pass `d.values()` straight inside the native `sum()` utility function.",
            solutionCode: `def sum_dict_values(d):\n    return sum(d.values())`,
            expectedOutput: "",
            testCases: [
              { input: "{'x': 10, 'y': 20}", call: "sum_dict_values({'x': 10, 'y': 20})", expected: "30" },
              { input: "{}",                 call: "sum_dict_values({})",                 expected: "0" },
            ],
          },
          {
            id: 99,
            title: "Find the Minimum of Three Numbers",
            slug: "python-find-minimum-of-three-numbers",
            seoTitle: "Python Min of Three Numbers | Built-in min Utility | Repractiq",
            metaDescription: "Find the smallest of three numbers using Python's native min utility function.",
            tags: ["math", "built-ins"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `min_of_three` that takes three numbers as arguments and returns the smallest one.",
            explanation: "The built-in `min()` function can accept multiple arguments directly and returns the minimum value among them.",
            hint: "Pass all three arguments directly into the native `min(a, b, c)` function.",
            solutionCode: `def min_of_three(a, b, c):\n    return min(a, b, c)`,
            expectedOutput: "",
            testCases: [
              { input: "10, 4, 8",   call: "min_of_three(10, 4, 8)",   expected: "4" },
              { input: "-1, 0, 2",   call: "min_of_three(-1, 0, 2)",   expected: "-1" },
            ],
          },
          {
            id: 100,
            title: "Calculate Simple Interest",
            slug: "python-calculate-simple-interest-formula",
            seoTitle: "Python Simple Interest Calculator | Financial Math | Repractiq",
            metaDescription: "Compute financial simple interest metrics given principal, rate, and time parameters.",
            tags: ["math", "arithmetic"],
            difficulty: "Easy",
            type: "function",
            description: "Write a function called `calculate_interest` that takes principal, rate (annual percentage), and time (years), and returns the calculated simple interest.",
            explanation: "The formula for simple interest is `(Principal * Rate * Time) / 100`.",
            hint: "Multiply principal, rate, and time together, then divide the result by 100.",
            solutionCode: `def calculate_interest(principal, rate, time):\n    return (principal * rate * time) / 100`,
            expectedOutput: "",
            testCases: [
              { input: "1000, 5, 2", call: "calculate_interest(1000, 5, 2)", expected: "100.0" },
              { input: "500, 3.5, 4", call: "calculate_interest(500, 3.5, 4)", expected: "70.0" },
            ],
          },
  ];